import { useState, useEffect } from "react";
import { Check, Clock, FileText, Users, ChartBar } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const Features = () => {
  const [currentMobileSlide, setCurrentMobileSlide] = useState(0);
  const [currentDesktopSlide, setCurrentDesktopSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const features = [
    {
      id: "schedule",
      value: "schedule",
      title: "Organize",
      icon: <Users className="h-auto w-4 shrink-0" />,
      content: {
        badge: "Client Management",
        heading: "Keep your clients organized",
        description: "Manage all your client information, projects, and communications in one place. Track engagement progress and never lose sight of important details.",
        buttonText: "Explore Client Management",
        imageSrc: "/images/engagements.png",
        imageAlt: "Client management interface showing project organization",
        checks: [
          "Centralized client database",
          "Project and task organization",
          "Communication history tracking",
          "Engagement progress monitoring"
        ]
      }
    },
    {
      id: "track",
      value: "track",
      title: "Track Time",
      icon: <Clock className="h-auto w-4 shrink-0" />,
      content: {
        badge: "Time Tracking",
        heading: "Track time effortlessly",
        description: "Start and stop timers with one click. Categorize time by client or project, and generate detailed reports for accurate billing.",
        buttonText: "See Time Tracking",
        imageSrc: "/images/timelogs.png",
        imageAlt: "Time tracking interface showing active timers",
        checks: [
          "One-click time tracking",
          "Project and client categorization",
          "Billable vs non-billable time",
          "Detailed time reports"
        ]
      }
    },
    {
      id: "invoice",
      value: "invoice",
      title: "Invoice",
      icon: <FileText className="h-auto w-4 shrink-0" />,
      content: {
        badge: "Invoicing",
        heading: "Create professional invoices",
        description: "Generate beautiful invoices in minutes. Automatically pull in time logs, track payments, and get paid faster with professional billing.",
        buttonText: "Try Invoice Generator",
        imageSrc: "/images/invoices.png",
        imageAlt: "Professional invoice generation interface",
        checks: [
          "Professional PDF invoices",
          "Automatic time log integration",
          "Payment tracking and reminders",
          "Custom branding options"
        ]
      }
    },
    {
      id: "report",
      value: "report",
      title: "Analytics",
      icon: <ChartBar className="h-auto w-4 shrink-0" />,
      content: {
        badge: "Analytics",
        heading: "Insights that drive growth",
        description: "Monitor your business performance with real-time analytics. Track revenue, analyze profitability, and make data-driven decisions.",
        buttonText: "View Dashboard",
        imageSrc: "/images/dashboard.png",
        imageAlt: "Business analytics dashboard showing key metrics",
        checks: [
          "Real-time business metrics",
          "Revenue and profitability tracking",
          "Time utilization insights",
          "Growth analytics"
        ]
      }
    }
  ];

  // Auto-advance carousel on mobile
  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentMobileSlide((prev) => (prev + 1) % features.length);
    }, 4000); // 4 seconds per slide

    return () => clearInterval(interval);
  }, [features.length, isPaused]);

  // Auto-advance carousel on desktop (separate state)
  useEffect(() => {
    if (isPaused) return;
    
    const interval = setInterval(() => {
      setCurrentDesktopSlide((prev) => (prev + 1) % features.length);
    }, 4000); // 4 seconds per slide

    return () => clearInterval(interval);
  }, [features.length, isPaused]);

  return (
    <section id="features" className="py-16 sm:py-24 bg-white dark:bg-white">
      <div className="container mx-auto px-4">
        <div className="flex flex-col items-center gap-4 text-center">
          <Badge variant="outline" className="!bg-white !border-gray-200">Features</Badge>
          <h2 className="max-w-3xl text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-900 md:text-4xl px-4 sm:px-0">
            Everything you need to run your business
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-600 max-w-2xl px-4 sm:px-0">
            Simple tools that work together to help you manage clients, track time, and get paid.
          </p>
        </div>

        {/* Desktop Auto-Advancing Carousel */}
        <div className="hidden sm:block">
          <div 
            className="mt-8 sm:mt-12"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {/* Feature indicators with highlighted line */}
            <div className="container flex items-center justify-center gap-4 md:gap-10 mb-8">
              {features.map((feature, index) => (
                <div
                  key={feature.value}
                  className={cn(
                    "flex items-center gap-2 rounded-xl px-4 md:px-5 py-3 text-sm font-semibold transition-all duration-300 cursor-pointer relative",
                    index === currentDesktopSlide 
                      ? "text-blue-600 dark:text-blue-600" 
                      : "text-gray-600 dark:text-gray-600 hover:text-gray-800 dark:hover:text-gray-800"
                  )}
                  onClick={() => setCurrentDesktopSlide(index)}
                >
                  {feature.icon} {feature.title}
                  
                  {/* Highlighted line indicator */}
                  <div 
                    className={cn(
                      "absolute -bottom-1 left-0 right-0 h-0.5 bg-blue-600 transition-all duration-300",
                      index === currentDesktopSlide ? "opacity-100" : "opacity-0"
                    )}
                  />
                </div>
              ))}
            </div>

            {/* Progress bar */}
            <div className="mb-6 mx-auto max-w-screen-xl">
              <div className="h-1 bg-gray-200 dark:bg-gray-200 rounded-full">
                <div 
                  className="h-full bg-blue-600 rounded-full transition-all duration-1000 ease-linear"
                  style={{ 
                    width: `${((currentDesktopSlide + 1) / features.length) * 100}%`
                  }}
                />
              </div>
            </div>
            
            {/* Content area - Fixed width container */}
            <div className="mx-auto max-w-screen-xl rounded-2xl bg-gray-50 dark:bg-gray-50 p-4 sm:p-6 lg:p-12">
              <div className="relative w-full overflow-hidden">
                <div 
                  className="flex transition-transform duration-500 ease-in-out"
                  style={{ 
                    transform: `translateX(-${currentDesktopSlide * (100 / features.length)}%)`,
                    width: `${features.length * 100}%`
                  }}
                >
                  {features.map((feature, index) => (
                    <div 
                      key={feature.value} 
                      className="w-full flex-shrink-0 grid place-items-center gap-8 sm:gap-12 lg:grid-cols-2 lg:gap-10"
                      style={{ width: `${100 / features.length}%` }}
                    >
                      <div className="flex flex-col gap-4 sm:gap-5">
                        {feature.id === "report" && (
                          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                            <Badge variant="outline" className="w-fit bg-blue-50 dark:bg-blue-50 text-blue-700 dark:text-blue-700 border-blue-200 dark:border-blue-200">
                              Available with Pro Tier
                            </Badge>
                          </div>
                        )}
                        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-900 lg:text-3xl">
                          {feature.content.heading}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-600 text-base sm:text-lg lg:text-lg">
                          {feature.content.description}
                        </p>
                        
                        <ul className="space-y-2 sm:space-y-3 mt-2">
                          {feature.content.checks.map((item, checkIndex) => (
                            <li key={checkIndex} className="flex items-start">
                              <Check className="text-blue-600 dark:text-blue-600 h-4 w-4 sm:h-5 sm:w-5 mr-2 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-700 dark:text-gray-700 text-sm sm:text-base">{item}</span>
                            </li>
                          ))}
                        </ul>
                        
                        <Button 
                          className="mt-3 sm:mt-4 w-full sm:w-fit !bg-blue-600 hover:!bg-blue-700 !text-white !border-0" 
                          size="lg"
                        >
                          {feature.content.buttonText}
                        </Button>
                      </div>
                      
                      <img
                        src={feature.content.imageSrc} 
                        alt={feature.content.imageAlt}
                        className="rounded-xl shadow-lg border border-gray-200 dark:border-gray-200 w-full h-auto max-h-[450px] object-cover"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Animated Carousel */}
        <div className="sm:hidden mt-8">
          <div 
            className="relative overflow-hidden rounded-2xl bg-gray-50 dark:bg-gray-50"
            onTouchStart={() => setIsPaused(true)}
            onTouchEnd={() => setIsPaused(false)}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {/* Progress bar */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200 z-10">
              <div 
                className="h-full bg-blue-600 transition-all duration-1000 ease-linear"
                style={{ 
                  width: `${((currentMobileSlide + 1) / features.length) * 100}%`
                }}
              />
            </div>

            {/* Slides container */}
            <div 
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentMobileSlide * 100}%)` }}
            >
              {features.map((feature, index) => (
                <div key={feature.value} className="w-full flex-shrink-0 p-6">
                  <div className="flex flex-col gap-6">
                    {/* Image first on mobile */}
                    <img
                      src={feature.content.imageSrc} 
                      alt={feature.content.imageAlt}
                      className="rounded-xl shadow-lg border border-gray-200 dark:border-gray-200 w-full h-auto max-h-[250px] object-cover"
                    />
                    
                    {/* Content */}
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-col items-start gap-2">
                        {feature.id === "report" && (
                          <Badge variant="outline" className="w-fit bg-blue-50 dark:bg-blue-50 text-blue-700 dark:text-blue-700 border-blue-200 dark:border-blue-200">
                            Available with Pro Tier
                          </Badge>
                        )}
                      </div>
                      
                      <h3 className="text-xl font-bold text-gray-900 dark:text-gray-900">
                        {feature.content.heading}
                      </h3>
                      
                      <p className="text-gray-600 dark:text-gray-600 text-base leading-relaxed">
                        {feature.content.description}
                      </p>
                      
                      <ul className="space-y-2">
                        {feature.content.checks.map((item, checkIndex) => (
                          <li key={checkIndex} className="flex items-start">
                            <Check className="text-blue-600 dark:text-blue-600 h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700 dark:text-gray-700 text-sm">{item}</span>
                          </li>
                        ))}
                      </ul>
                      
                      <Button 
                        className="mt-3 w-full !bg-blue-600 hover:!bg-blue-700 !text-white !border-0" 
                        size="lg"
                      >
                        {feature.content.buttonText}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Slide indicators */}
            <div className="flex justify-center pb-6 gap-2">
              {features.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentMobileSlide(index)}
                  className={cn(
                    "w-2 h-2 rounded-full transition-all duration-300",
                    index === currentMobileSlide 
                      ? "bg-blue-600 w-6" 
                      : "bg-gray-300"
                  )}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features; 